import { useEffect, useState, useCallback, useMemo, useRef } from 'react';

import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useWaveformRecorder } from '@/hooks/useWaveformRecorder';
import type { IntegrationType } from '@/shared/types/integration';
import { MAX_MEDIA_UPLOAD_SIZE } from '@/util/constants';
import { preventClickThrough } from '@/util/util';

import { convertFile, dropzoneAcceptConfig, prepareFileUpload, uploadFile } from '../helpers';

const MAX_FILES = 4;

export type UploadedData = {
  name: string;
  id: string;
  type: 'file' | 'audio' | 'image' | 'video';
  original_name: string;
  mime_type: string;
};

type CreatePresignedUrlFn = (params: {
  file_id: string;
  file_type: string;
  original_name: string;
}) => Promise<{ upload_url: string; file_name: string; file_id: string }>;

type UseFileUploadOptions = {
  channel: IntegrationType | undefined;
  createPresignedUrl: CreatePresignedUrlFn;
};

export const useFileUpload = ({ channel, createPresignedUrl }: UseFileUploadOptions) => {
  const { t } = useTranslation();

  // File state
  const [files, setFiles] = useState([]);
  const [uploadedData, setUploadedData] = useState<UploadedData[]>([]);
  const [uploading, setUploading] = useState(false);

  // Voice recording
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const {
    containerRef,
    startRecording,
    stopRecording,
    recordingBlob,
    setRecordingBlob,
    isRecording,
    recordingTime,
    isTranscoding,
    totalRecordingTime,
    isPlaying,
    currentTime,
    duration,
    playPause,
    reset,
  } = useWaveformRecorder();

  const audioUrlRef = useRef(audioUrl);
  audioUrlRef.current = audioUrl;

  const createPresignedUrlRef = useRef(createPresignedUrl);
  createPresignedUrlRef.current = createPresignedUrl;
  const channelRef = useRef(channel);
  channelRef.current = channel;

  const clearUploads = useCallback(() => {
    reset();
    setFiles([]);
    setUploadedData([]);
    setRecordingBlob(undefined);
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
    }
    setAudioUrl(null);
  }, [reset, setRecordingBlob]);

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        rejectedFiles.forEach((rejection) => {
          const error = rejection.errors?.[0];
          let errorMessage: string;
          if (error?.code === 'file-too-large') {
            errorMessage = t('chatBox.file_too_large');
          } else {
            errorMessage = error?.message || 'Unknown error';
          }
          toast.error(`${rejection.file.name}: ${errorMessage}`);
        });
      }

      if (!acceptedFiles.length) {
        return;
      }

      const canUpload = uploadedData.length + acceptedFiles.length <= MAX_FILES;
      if (!canUpload) {
        toast.error(t('chatBox.upload_error', { 0: MAX_FILES }));
        return;
      }

      setUploading(true);

      try {
        const preparedFiles = acceptedFiles.map((file) => prepareFileUpload(file));
        setFiles((oldFiles) => [...oldFiles, ...preparedFiles]);

        const uploadedFilesData = [];

        for (const file of preparedFiles) {
          try {
            const fileToUpload = await convertFile(file, channel);
            const uploadedDataItem = await uploadFile(
              fileToUpload,
              file.fileId,
              createPresignedUrl,
              channel
            );
            uploadedFilesData.push(uploadedDataItem);
          } catch (fileError) {
            setFiles([]);
            setUploadedData([]);
            toast.error(`Failed to upload ${file.name}: ${fileError.message}`);
            throw fileError;
          }
        }

        setUploadedData((prev) => [...prev, ...uploadedFilesData]);
      } catch (error) {
        console.error('File upload error:', error);
      } finally {
        setUploading(false);
      }
    },
    [uploadedData.length, t, channel, createPresignedUrl]
  );

  const dropzoneConfig = useMemo(
    () => ({
      accept: dropzoneAcceptConfig,
      maxSize: MAX_MEDIA_UPLOAD_SIZE,
      disabled: uploading,
      multiple: true,
      noDragEventsBubbling: true,
      onDrop,
      noClick: true,
      noKeyboard: true,
      noPaste: true,
      validator: (_file) => {
        if (uploadedData.length > MAX_FILES) {
          return { code: 'too-many-files', message: t('chatBox.upload_error', { 0: MAX_FILES }) };
        }
        return null;
      },
    }),
    [uploading, onDrop, uploadedData.length, t]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone(dropzoneConfig);

  const handleOnRemoveFile = useCallback((e, index: number, fileId: string) => {
    preventClickThrough(e);
    setFiles((files) => files.filter((_, i) => i !== index));
    setUploadedData((f) => f.filter((f) => f.id !== fileId));
  }, []);

  const handlePasteImage = useCallback(async () => {
    try {
      if (uploading) {
        return;
      }

      if (uploadedData.length >= MAX_FILES) {
        toast.error(t('chatBox.upload_error', { 0: MAX_FILES }));
        return;
      }
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (type.startsWith('image')) {
            if (uploadedData.length + 1 > MAX_FILES) {
              toast.error(t('chatBox.upload_error', { 0: MAX_FILES }));
              return;
            }
            setUploading(true);
            const blob = await clipboardItem.getType(type);
            const extension = blob.type.split('/')[1] || 'jpg';
            const file = new File([blob], `${Date.now()}.${extension}`, { type: blob.type });

            const prepared = prepareFileUpload(file);
            setFiles((prev) => [...prev, prepared]);

            const imageData = await uploadFile(file, prepared.fileId, createPresignedUrl, channel);
            setUploadedData((prev) => [...prev, imageData]);
            setUploading(false);
          }
        }
      }
    } catch (readErr) {
      console.warn('[ChatBox]Failed to read clipboard items', readErr);
      setUploading(false);
    }
  }, [uploading, uploadedData.length, t, createPresignedUrl, channel]);

  // Upload audio once the recorder finishes transcoding
  useEffect(() => {
    if (!recordingBlob || audioUrl || isTranscoding) {
      return;
    }

    const url = URL.createObjectURL(recordingBlob);
    setAudioUrl(url);

    const sendAudio = async () => {
      const extension = recordingBlob.type.split('/')[1];
      const audioFile = new File([recordingBlob], `${Date.now()}.${extension}`, {
        type: recordingBlob.type,
      });
      const prepared = prepareFileUpload(audioFile);

      setUploading(true);
      try {
        const audioData = await uploadFile(
          audioFile,
          prepared.fileId,
          createPresignedUrlRef.current,
          channelRef.current
        );

        setUploadedData((prev) => [...prev, audioData]);
        setFiles((prev) => [
          ...prev,
          Object.assign(audioFile, {
            preview: URL.createObjectURL(audioFile),
            path: audioFile.name,
          }),
        ]);
      } catch (error) {
        console.error('Audio upload error:', error);
        toast.error(`Failed to upload audio: ${error.message}`);
      } finally {
        setUploading(false);
      }
    };

    sendAudio();
  }, [recordingBlob, audioUrl, isTranscoding]);

  const handleStartRecording = useCallback(() => {
    setFiles([]);
    setUploadedData([]);
    startRecording();
  }, [startRecording]);

  return {
    files,
    setFiles,
    uploading,
    uploadedData,
    clearUploads,
    getRootProps,
    getInputProps,
    open,
    isDragActive,
    handleOnRemoveFile,
    handlePasteImage,
    audioUrl,
    handleStartRecording,
    handleStopRecording: stopRecording,
    isRecording,
    recordingBlob,
    recordingTime,
    isTranscoding,
    totalRecordingTime,
    containerRef,
    isPlaying,
    currentTime,
    duration,
    playPause,
  };
};
